import type { ClickEventNameEnum } from './ClickEventNameEnum';
import type { ContextEnum } from './ContextEnum';
import type { TargetEnum } from './TargetEnum';
import type { HotelImageActionTypeEnum } from './HotelImageActionTypeEnum';

export const booleanValue = (value: string): number => (value === '1' ? 1 : 0);

export const removeExtraComma = (value: string): string =>
  value
    .split(',')
    .filter((x) => x !== '')
    .join(',');

export const validateCommaList = (value: string): boolean => /^\w(\s*,?\s*\w)*$/.test(removeExtraComma(value));

export const integerValues = (value: string): { min: number; max: number } => {
  const values = value.split(',');
  return { min: parseInt(values[0], 10), max: parseInt(values[1], 10) };
};

export type CommonParams = {
  sort?: string;
  group?: number;
  ab_test?: string;
  traffic_subsource?: string;
  traffic_source?: string;
};

export type SearchWidgetParams = {
  people_adults?: number;
  people_child?: number;
  people_infant?: number;
  departure_date_from?: string;
  departure_date_to?: string;
  return_date_from?: string;
  return_date_to?: string;
  nights_from?: number;
  nights_to?: number;
  destination?: string;
  leisure?: string;
  origin?: string;
  source?: string;
  subsource?: string;
};

export type FiltersParams = {
  filters_applied?: number;
  filters_values_direct?: number;
  filters_values_hotel_stars?: string;
  filters_values_meal?: string;
  filters_values_rating?: string;
  filters_values_price_from?: number;
  filters_values_price_to?: number;
  filters_flash_sales?: number;
  filters_alt_accomod?: number;
  filters_values_iand?: number;
  filters_free_cancel?: number;
  filters_book_now_pay_later?: number;
  filters_values_flight_class?: string;
  filters_values_outbound_connections?: string;
  filters_values_return_connections?: string;
  filters_values_departure_time_outbound_from?: number;
  filters_values_departure_time_outbound_to?: number;
  filters_values_return_time_outbound_from?: number;
  filters_values_return_time_outbound_to?: number;
  filters_values_flight_duration_from?: number;
  filters_values_flight_duration_to?: number;
  filters_values_outbound_departure_airports?: string;
  filters_values_outbound_arrival_airports?: string;
  filters_values_return_departure_airports?: string;
  filters_values_return_arrival_airports?: string;
  filters_values_airlines?: string;
  filters_values_departure_flights?: number;
  filters_values_return_flights?: number;
  filters_values_travel_flexibility?: number;
  filters_values_accomodation_type?: string;
  filters_values_accomodation_facilities?: string;
  filters_leisure_blue_flag?: number;
  filters_sustainability_certificate?: number;
  filters_leisure_beach_type?: string;
  filters_leisure_climate?: string;
  filters_leisure_fauna?: string;
  filters_leisure_watersport?: string;
  filters_leisure_entertainments?: string;
  filters_leisure_waterwaves?: string;
  filters_values_myprice?: number;
  filters_values_top_secret_hotel?: number;
  filters_values_accomodation_beach?: string;
  filters_values_accomodation_skilift?: string;
  filters_values_accomodation_district?: string;
  filters_values_accomodation_station?: string;
  filters_values_accomodation_chain?: string;
  filters_values_accomodation_theme?: string;
  filters_values_accomodation_name?: string;
  filters_overnight_selected?: number;
};

export type RedirectParams = {
  redirect_type: string;
  trace_id: string;
  offer_origin?: string;
  offer_destination_internal_id_city?: number;
  offer_internal_id_hotel?: number;
  offer_departure_date?: string;
  offer_return_date?: string;
  offer_checkin_date?: string;
  offer_checkout_date?: string;
  offer_nights?: number;
  offer_currency?: string;
  offer_price_displayed?: number;
  offer_price_total?: number;
  offer_origin_airport?: string;
  offer_destination_airport?: string;
  offer_flight_class?: string;
};

export type ClickParams = {
  event_name: ClickEventNameEnum | string;
  event_target?: TargetEnum;
  event_context?: ContextEnum | string;
  event_subcontext?: string;
  event_value?: string;
  event_value_additional?: string;
};

export type PageViewParams = {
  url: string;
  referrer_url?: string;
  event_target: TargetEnum;
  event_context: ContextEnum;
};

export type RedirectWidgetParams = {
  redirect_type: string;
  trace_id: string;
  origin?: string;
  destination: string;
  people_adults: number;
  people_child: number;
  people_infant: number;
  departure_date_from: string;
  departure_date_to: string;
  return_date_from?: string;
  return_date_to?: string;
  nights_from?: number;
  nights_to?: number;
  group: number;
  sort: string;
  filters_applied: number;
};

export type InvalidDeepLinkParams = {
  url: string;
  referrer_url?: string;
  invalid_value: string;
};

export type RankingParams = {
  vc_search_id: number; // live search id from memsearch, use 0 for open search
  is_live_search: number; // 1 or 0
  is_flexible_destination: number; // 1 or 0
  hotel_id: number;
  hotel_city_id: number;
  hotel_provider_id: number;
  opx_sort_priority: number; // 0 (no), 1 (DP_FIRST), 2 (TO_FIRST)
  opx_deal_type: number; // 0 (no), 1 (wegde), 2 (orchestra)
  is_fallback: number; // 1 or 0
  business_profile: string;
  product: string; // 'DP', 'HO' etc (uppercased)
  ranking_type: string; // 'MSRP+RTCO (FS) + Priority', 'DMSRP' etc
  ranking_destination: number;
  ranking_manual: number;
  ranking_psq: number;
  ranking_sa: number;
  ranking_sa_type: string;
  ranking_pos_original: number;
  ranking_pos_adjusted: number;
  ranking_pos_absolute: number;
  hotel_is_highlighted: number; // 1 or 0
  rtco_enabled: number; // 1 or 0
  rtco_score: number;
  rtco_variation: number;
  nights: number;
  offer_price_total: number;
  promotions: string; // Promo, joined with comma (1 - fs, 2 - myprice, 3 - package disc)
};

export type HotelImagesParams = {
  url: string;
  hotel_id: number;
  hotel_city_id: number;
  action_type: HotelImageActionTypeEnum;
  swipe_index: number;
  nights: number;
  offer_price_total: number;
  promotions: string;
};
